import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import './EditRecipe.css';
import PageHeader from './PageHeader';
import ConfirmationModal from './ConfirmationModal';
import useConfirmation from '../hooks/useConfirmation';
import { validateRecipeForm } from '../utils/recipeFormUtils';

function EditRecipe() {
  const { recipeId } = useParams();
  const navigate = useNavigate();
  const { confirmationState, showConfirmation, handleConfirm, handleCancel: handleConfirmCancel } = useConfirmation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // États pour les données de la recette
  const [recipeName, setRecipeName] = useState('');
  const [description, setDescription] = useState('');
  const [prepTime, setPrepTime] = useState('');
  const [cookTime, setCookTime] = useState('');
  const [servings, setServings] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [source, setSource] = useState('');
  const [note, setNote] = useState(0);

  // États pour les catégories
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('');


  // États pour le sélecteur de catégories amélioré
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState([]);

  // États pour les ingrédients et étapes
  const [ingredients, setIngredients] = useState([]);
  const [steps, setSteps] = useState([]);

  // Partie 2 : Effects et chargement initial (useEffect)
  // Charger les données de la recette
  useEffect(() => {
    const fetchRecipeData = async () => {
      if (!auth.currentUser) {
        navigate('/');
        return;
      }

      try {
        setLoading(true);

        // Charger catégories et recette en parallèle
        const recipeRef = doc(
          db,
          'recettesUtilisateur',
          auth.currentUser.uid,
          'recettes',
          recipeId
        );
        const [recipeSnap] = await Promise.all([
          getDoc(recipeRef),
          loadCategories(),
        ]);

        if (!recipeSnap.exists()) {
          setMessage({
            type: 'error',
            text: 'Recette non trouvée',
          });
          navigate('/recipes');
          return;
        }

        const recipeData = recipeSnap.data();

        // Initialiser les états avec les données de la recette
        setRecipeName(recipeData.nom || '');
        setDescription(recipeData.description || '');
        setPrepTime(recipeData.tempsPreparation?.toString() || '');
        setCookTime(recipeData.tempsCuisson?.toString() || '');
        setServings(recipeData.nombrePersonnes?.toString() || '');
        setDifficulty(recipeData.difficulte || '');
        setSource(recipeData.source || '');
        setNote(recipeData.note || 0);
        setSelectedCategories(recipeData.categories || []);

        // Initialiser les ingrédients
        if (recipeData.ingredients && recipeData.ingredients.length > 0) {
          setIngredients(
            recipeData.ingredients.map((ing, index) => ({
              id: index + 1,
              name: ing.nom || '',
              quantity: ing.quantite?.toString() || '',
              unit: ing.unite || '',
              comment: ing.commentaire || '',
            }))
          );
        } else {
          setIngredients([
            { id: 1, name: '', quantity: '', unit: '', comment: '' },
          ]);
        }

        // Initialiser les étapes
        if (
          recipeData.etapesPreparation &&
          recipeData.etapesPreparation.length > 0
        ) {
          setSteps(
            recipeData.etapesPreparation.map(step => ({
              id: step.numero,
              description: step.description || '',
              timeEstimate: step.tempsEstime?.toString() || '',
            }))
          );
        } else {
          setSteps([{ id: 1, description: '', timeEstimate: '' }]);
        }

        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement de la recette:', error);
        setMessage({
          type: 'error',
          text: 'Erreur lors du chargement de la recette',
        });
        setLoading(false);
      }
    };

    fetchRecipeData();
  }, [recipeId, navigate]);

  // Effet pour filtrer les catégories en fonction de la recherche
  useEffect(() => {
    if (categorySearchTerm.trim() === '') {
      setFilteredCategories(categories);
    } else {
      const filtered = categories.filter(category =>
        category.nom.toLowerCase().includes(categorySearchTerm.toLowerCase())
      );
      setFilteredCategories(filtered);
    }
  }, [categorySearchTerm, categories]);

  // Fermer le dropdown lorsque l'utilisateur clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        showCategoryDropdown &&
        !event.target.closest('.category-input-container') &&
        !event.target.closest('.new-category-form')
      ) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCategoryDropdown]);

  // Ajuster la hauteur des textareas lors du chargement
  useEffect(() => {
    if (!loading) {
      const textareas = document.querySelectorAll('textarea');
      textareas.forEach(textarea => {
        adjustTextareaHeight(textarea);
      });
    }
  }, [loading, ingredients, steps]);

  // Partie 3 : Fonctions utilitaires
  // Charger les catégories de l'utilisateur
  const loadCategories = async () => {
    try {
      if (!auth.currentUser) return;

      const categoriesCollection = collection(
        db,
        'categoriesUtilisateur',
        auth.currentUser.uid,
        'categories'
      );

      const querySnapshot = await getDocs(categoriesCollection);
      const categoriesList = [];

      querySnapshot.forEach(doc => {
        categoriesList.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      // Trier par ordre si disponible
      categoriesList.sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
      setCategories(categoriesList);
      setFilteredCategories(categoriesList);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
      setMessage({
        type: 'error',
        text: 'Impossible de charger vos catégories',
      });
    }
  };

  // Fonction pour ajuster automatiquement la hauteur des textareas
  const adjustTextareaHeight = element => {
    if (!element) return;

    // Réinitialiser la hauteur pour obtenir la bonne hauteur de scrollHeight
    element.style.height = 'auto';

    // Définir la hauteur en fonction du contenu
    element.style.height = `${element.scrollHeight}px`;
  };

  // Basculer la sélection d'une catégorie
  const toggleCategory = categoryId => {
    if (selectedCategories.includes(categoryId)) {
      setSelectedCategories(selectedCategories.filter(id => id !== categoryId));
    } else {
      setSelectedCategories([...selectedCategories, categoryId]);
    }
  };

  // Gérer la modification d'un ingrédient
  const handleIngredientChange = (id, field, value) => {
    setIngredients(
      ingredients.map(ing => (ing.id === id ? { ...ing, [field]: value } : ing))
    );
  };

  // Gérer l'ajout d'un nouvel ingrédient
  const handleAddIngredient = () => {
    const newId = Math.max(...ingredients.map(ing => ing.id), 0) + 1;
    setIngredients([
      ...ingredients,
      { id: newId, name: '', quantity: '', unit: '', comment: '' },
    ]);
  };

  // Gérer la suppression d'un ingrédient
  const handleRemoveIngredient = id => {
    if (ingredients.length <= 1) return;
    setIngredients(ingredients.filter(ing => ing.id !== id));
  };

  // Gérer la modification d'une étape
  const handleStepChange = (id, field, value) => {
    setSteps(
      steps.map(step => (step.id === id ? { ...step, [field]: value } : step))
    );
  };

  // Gérer l'ajout d'une nouvelle étape
  const handleAddStep = () => {
    const newId = Math.max(...steps.map(step => step.id), 0) + 1;
    setSteps([...steps, { id: newId, description: '', timeEstimate: '' }]);
  };

  // Gérer la suppression d'une étape
  const handleRemoveStep = id => {
    if (steps.length <= 1) return;
    setSteps(steps.filter(step => step.id !== id));
  };

  // Icône de poubelle SVG
  const TrashIcon = () => (
    <svg
      className="trash-icon"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 6h18"></path>
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
    </svg>
  );

  // Partie 4 : Fonctions de manipulation des catégories
  // Supprimer une catégorie
  const handleDeleteCategory = (categoryId, e) => {
    e.stopPropagation();
    const category = categories.find(c => c.id === categoryId);
    if (!category) return;

    showConfirmation({
      title: 'Supprimer cette catégorie',
      message: `La catégorie "${category.nom}" sera supprimée définitivement.`,
      details: (
        <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '8px' }}>
          Les recettes qui l'utilisent ne seront pas supprimées, mais perdront cette catégorie.
        </div>
      ),
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      type: 'danger',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'categoriesUtilisateur', auth.currentUser.uid, 'categories', categoryId));
          setCategories(prev => prev.filter(c => c.id !== categoryId));
          setSelectedCategories(prev => prev.filter(id => id !== categoryId));
        } catch (err) {
          setMessage({ type: 'error', text: 'Erreur lors de la suppression de la catégorie.' });
        }
      },
    });
  };

  // Ajouter une nouvelle catégorie
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      setMessage({
        type: 'error',
        text: 'Veuillez entrer un nom pour la catégorie',
      });
      return;
    }

    if (!newCategoryColor) {
      setMessage({
        type: 'error',
        text: 'Veuillez choisir une couleur pour la catégorie',
      });
      return;
    }

    try {
      // Créer la référence à la collection des catégories de l'utilisateur
      const categoriesCollection = collection(
        db,
        'categoriesUtilisateur',
        auth.currentUser.uid,
        'categories'
      );

      // Déterminer l'ordre de la nouvelle catégorie
      const newOrder =
        categories.length > 0
          ? Math.max(...categories.map(cat => cat.ordre || 0)) + 1
          : 0;

      // Ajouter la nouvelle catégorie
      const newCategoryData = {
        nom: newCategoryName.trim(),
        couleur: newCategoryColor,
        dateCreation: serverTimestamp(),
        ordre: newOrder,
      };

      const newCategoryRef = await addDoc(
        categoriesCollection,
        newCategoryData
      );

      // Mettre à jour l'état local
      const newCategory = {
        id: newCategoryRef.id,
        ...newCategoryData,
      };

      setCategories([...categories, newCategory]);
      setSelectedCategories([...selectedCategories, newCategoryRef.id]);

      // Réinitialiser le formulaire
      setNewCategoryName('');
      setNewCategoryColor('');
      setShowNewCategoryForm(false);
      setShowCategoryDropdown(false);
      setCategorySearchTerm('');

      setMessage({
        type: 'success',
        text: 'Catégorie ajoutée avec succès',
      });

      // Effacer le message après quelques secondes
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error("Erreur lors de l'ajout de la catégorie:", error);
      setMessage({
        type: 'error',
        text: "Erreur lors de l'ajout de la catégorie",
      });
    }
  };

  // Annuler l'édition
  const handleCancel = () => {
    navigate(`/recipe/${recipeId}`);
  };
  // Partie 5 : Fonctions de validation et sauvegarde
  // Sauvegarder les modifications de la recette
  const saveRecipe = async () => {
    const { valid, error } = validateRecipeForm(recipeName, servings, ingredients);
    if (!valid) {
      setMessage({ type: 'error', text: error });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      // Préparer les données mises à jour
      const updatedRecipeData = {
        nom: recipeName.trim(),
        description: description.trim(),
        tempsPreparation: prepTime ? parseInt(prepTime) : 0,
        tempsCuisson: cookTime ? parseInt(cookTime) : 0,
        nombrePersonnes: servings ? parseInt(servings) : 0,
        difficulte: difficulty || null,
        note: note || 0,
        categories: selectedCategories,
        ingredients: ingredients.map(ing => ({
          nom: ing.name.trim(),
          quantite: ing.quantity.trim() || null,
          unite: ing.unit.trim(),
          commentaire: ing.comment.trim(),
        })),
        etapesPreparation: steps.map((step, index) => ({
          numero: index + 1,
          description: step.description.trim(),
          ...(step.timeEstimate ? { tempsEstime: parseInt(step.timeEstimate) } : {}),
        })),
        dateMiseAJour: serverTimestamp(),
      };

      // Mettre à jour le document
      const recipeRef = doc(
        db,
        'recettesUtilisateur',
        auth.currentUser.uid,
        'recettes',
        recipeId
      );

      await updateDoc(recipeRef, updatedRecipeData);

      setMessage({
        type: 'success',
        text: 'Recette mise à jour avec succès!',
      });

      // Rediriger vers la page de détail de la recette après un court délai
      setTimeout(() => {
        navigate(`/recipe/${recipeId}`);
      }, 1500);
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la recette:', error);
      setMessage({
        type: 'error',
        text: 'Erreur lors de la mise à jour de la recette',
      });
    } finally {
      setSaving(false);
    }
  };
  // Partie 6.1 Rendu de l'interface (render/return)
  if (loading) {
    return <div className="loading">Chargement de la recette...</div>;
  }

  return (
    <div className="edit-recipe-container">
      <PageHeader
        title="Modifier la recette"
        showBackButton={false}
        sticky={true}
        actions={
          <>
            <button className="cancel-button" onClick={handleCancel}>
              Annuler
            </button>
            <button
              className="save-button"
              onClick={saveRecipe}
              disabled={saving}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </>
        }
      />

      {message.text && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      {/* Section Informations générales */}
      <div className="form-section">
        <div className="form-section-header">
          <h2 className="form-section-title">Informations générales</h2>
        </div>

        {/* Sélecteur de catégories amélioré */}
        <div className="category-selector">
          <label
            style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}
          >
            Catégories
          </label>

          <div className="category-input-container">
            <div
              className="category-input-wrapper"
              onClick={() => setShowCategoryDropdown(true)}
            >
              {/* Catégories sélectionnées sous forme de chips */}
              {selectedCategories.map(catId => {
                const category = categories.find(c => c.id === catId);
                return category ? (
                  <div
                    key={category.id}
                    className="category-chip"
                    style={{ backgroundColor: category.couleur }}
                  >
                    {category.nom}
                    <button
                      className="remove-category"
                      onClick={e => {
                        e.stopPropagation();
                        toggleCategory(category.id);
                      }}
                    >
                      ×
                    </button>
                  </div>
                ) : null;
              })}

              {/* Champ de recherche */}
              <input
                type="text"
                className="category-input"
                placeholder="Rechercher ou ajouter une catégorie"
                value={categorySearchTerm}
                onChange={e => setCategorySearchTerm(e.target.value)}
                onFocus={() => setShowCategoryDropdown(true)}
              />
            </div>

            {/* Dropdown des résultats */}
            {showCategoryDropdown && (
              <div className="category-dropdown">
                {/* Bouton créer toujours en haut */}
                <div
                  className="add-new-category"
                  onClick={() => {
                    setNewCategoryName(categorySearchTerm);
                    setShowNewCategoryForm(true);
                    setShowCategoryDropdown(false);
                  }}
                >
                  <span>+</span> Nouvelle catégorie
                </div>

                {filteredCategories.length > 0 ? (
                  <div className="category-list">
                    {filteredCategories.map(category => (
                      <div
                        key={category.id}
                        className={`category-option ${
                          selectedCategories.includes(category.id) ? 'active' : ''
                        }`}
                        onClick={() => toggleCategory(category.id)}
                        style={
                          selectedCategories.includes(category.id)
                            ? { backgroundColor: category.couleur }
                            : {}
                        }
                      >
                        <div className="color-dot" style={{ backgroundColor: category.couleur }}></div>
                        <span>{category.nom}</span>
                        {selectedCategories.includes(category.id) && (
                          <span className="check">✓</span>
                        )}
                        <button
                          className="delete-category-btn"
                          onClick={e => handleDeleteCategory(category.id, e)}
                          title="Supprimer cette catégorie"
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-results">
                    <span>Aucune catégorie trouvée{categorySearchTerm ? ` pour "${categorySearchTerm}"` : ''}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {showNewCategoryForm && (
          <div className="new-category-form">
            <div className="category-creator">
              <div className="new-category-name-field">
                <label className="new-category-label">Nom de la catégorie</label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={e => setNewCategoryName(e.target.value)}
                  placeholder="Ex : Desserts, Végétarien..."
                  autoFocus
                  className="new-category-name-input"
                  onKeyDown={e => e.key === 'Enter' && handleAddCategory()}
                />
              </div>

              {newCategoryColor && newCategoryName && (
                <div className="category-preview-container">
                  <div
                    className="category-preview has-color"
                    style={{ backgroundColor: newCategoryColor }}
                  >
                    <span>{newCategoryName}</span>
                  </div>
                </div>
              )}

              <div className="color-selector">
                <p>Choisissez une couleur pour votre catégorie :</p>
                <div className="color-options">
                  {[
                    '#FF5252', // Rouge
                    '#FF7043', // Orange
                    '#8D6E63', // Marron
                    '#5C6BC0', // Bleu
                    '#7C4DFF', // Violet
                    '#009688', // Turquoise
                    '#2E7D32', // Vert foncé
                    '#C2185B', // Rose foncé
                  ].map((color, index) => (
                    <button
                      key={index}
                      className={`color-option ${
                        newCategoryColor === color ? 'selected' : ''
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setNewCategoryColor(color)}
                      aria-label={`Couleur ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="button-group">
              <button
                className="action-button"
                onClick={handleAddCategory}
                disabled={!newCategoryName.trim() || !newCategoryColor}
              >
                Ajouter la catégorie
              </button>
              <button
                className="cancel-button small"
                onClick={() => setShowNewCategoryForm(false)}
              >
                Annuler
              </button>
            </div>
          </div>
        )}

        <div className="input-group">
          <label>Nom de la recette *</label>
          <input
            type="text"
            value={recipeName}
            onChange={e => setRecipeName(e.target.value)}
            placeholder="Ex: Tarte aux pommes"
            required
          />
        </div>

        <div className="input-group">
          <label>Description</label>
          <textarea
            value={description}
            onChange={e => {
              setDescription(e.target.value);
              adjustTextareaHeight(e.target);
            }}
            placeholder="Une description rapide de la recette"
            rows="3"
          />
        </div>

        <div className="input-row">
          <div className="input-group">
            <label>Temps de préparation (minutes)</label>
            <input
              type="number"
              value={prepTime}
              onChange={e => setPrepTime(e.target.value)}
              min="0"
              placeholder="Ex: 20"
            />
          </div>

          <div className="input-group">
            <label>Temps de cuisson (minutes)</label>
            <input
              type="number"
              value={cookTime}
              onChange={e => setCookTime(e.target.value)}
              min="0"
              placeholder="Ex: 30"
            />
          </div>

          <div className="input-group">
            <label>Nombre de personnes</label>
            <input
              type="number"
              value={servings}
              onChange={e => setServings(e.target.value)}
              min="0"
              placeholder="Ex: 4"
            />
          </div>
        </div>

        <div className="input-group">
          <label>Difficulté</label>
          <div className="difficulty-selector">
            <button
              type="button"
              className={difficulty === 'Facile' ? 'selected' : ''}
              onClick={() => setDifficulty('Facile')}
            >
              Facile
            </button>
            <button
              type="button"
              className={difficulty === 'Moyen' ? 'selected' : ''}
              onClick={() => setDifficulty('Moyen')}
            >
              Moyen
            </button>
            <button
              type="button"
              className={difficulty === 'Difficile' ? 'selected' : ''}
              onClick={() => setDifficulty('Difficile')}
            >
              Difficile
            </button>
          </div>
        </div>

        <div className="input-group">
          <label>Source / Origine</label>
          <input
            type="text"
            value={source}
            onChange={e => setSource(e.target.value)}
            placeholder="Ex: Livre de cuisine, site web, tradition familiale..."
          />
        </div>

        <div className="input-group">
          <label>Ma note</label>
          <div className="star-picker">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                type="button"
                className={`star-picker-btn ${star <= note ? 'star-picker-filled' : 'star-picker-empty'}`}
                onClick={() => setNote(note === star ? 0 : star)}
                aria-label={`${star} étoile${star > 1 ? 's' : ''}`}
              >
                <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
              </button>
            ))}
            {note > 0 && (
              <button type="button" className="star-clear-btn" onClick={() => setNote(0)}>
                Effacer
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Section Ingrédients */}
      <div className="form-section">
        <div className="form-section-header">
          <h2 className="form-section-title">Ingrédients</h2>
        </div>

        {/* Partie 6.2 Rendu de l'interface (render/return) */}
        {ingredients.map(ingredient => (
          <div key={ingredient.id} className="ingredient-row">
            <button
              type="button"
              className="remove-button"
              onClick={() => handleRemoveIngredient(ingredient.id)}
              disabled={ingredients.length <= 1}
              aria-label="Supprimer l'ingrédient"
            >
              <TrashIcon />
            </button>
            <div className="ingredient-header">
              <div className="input-row">
                <div className="input-group ingredient-quantity">
                  <label>Quantité</label>
                  <input
                    type="text"
                    value={ingredient.quantity}
                    onChange={e =>
                      handleIngredientChange(
                        ingredient.id,
                        'quantity',
                        e.target.value
                      )
                    }
                    placeholder="200, 1 filet..."
                  />
                </div>
                <div className="input-group ingredient-unit">
                  <label>Unité</label>
                  <input
                    type="text"
                    value={ingredient.unit}
                    onChange={e =>
                      handleIngredientChange(
                        ingredient.id,
                        'unit',
                        e.target.value
                      )
                    }
                    placeholder="Ex: g"
                  />
                </div>
                <div className="input-group ingredient-name">
                  <label>Nom de l'ingrédient *</label>
                  <input
                    type="text"
                    value={ingredient.name}
                    onChange={e =>
                      handleIngredientChange(
                        ingredient.id,
                        'name',
                        e.target.value
                      )
                    }
                    placeholder="Ex: Farine"
                    required
                  />
                </div>
              </div>
            </div>
            <div className="input-group ingredient-comment">
              <label>Commentaire (optionnel)</label>
              <input
                type="text"
                value={ingredient.comment}
                onChange={e =>
                  handleIngredientChange(
                    ingredient.id,
                    'comment',
                    e.target.value
                  )
                }
                placeholder="Ex: tamisée, à température ambiante..."
              />
            </div>
          </div>
        ))}

        <button
          type="button"
          className="add-button"
          onClick={handleAddIngredient}
        >
          + Ajouter un ingrédient
        </button>
      </div>

      {/* Section Étapes de préparation */}
      <div className="form-section">
        <div className="form-section-header">
          <h2 className="form-section-title">Étapes de préparation
          </h2>
        </div>

        {steps.map((step, index) => (
          <div key={step.id} className="step-row">
            <button
              type="button"
              className="remove-button"
              onClick={() => handleRemoveStep(step.id)}
              disabled={steps.length <= 1}
              aria-label="Supprimer l'étape"
            >
              <TrashIcon />
            </button>
            <div className="step-number">{index + 1}</div>
            <div className="step-content">
              <div className="input-group">
                <label>Description de l'étape</label>
                <textarea
                  value={step.description}
                  onChange={e => {
                    handleStepChange(step.id, 'description', e.target.value);
                    adjustTextareaHeight(e.target);
                  }}
                  placeholder="Ex: Mélanger les ingrédients secs dans un bol..."
                  rows="2"
                />
              </div>
            </div>
          </div>
        ))}

        <button type="button" className="add-button" onClick={handleAddStep}>
          + Ajouter une étape
        </button>
      </div>

      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        onConfirm={handleConfirm}
        onCancel={handleConfirmCancel}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        type={confirmationState.type}
        details={confirmationState.details}
      />
    </div>
  );
}

export default EditRecipe;
